import React from 'react';
import styled from '@emotion/styled';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { approvalFilter } from '@/recoil/atom';
import { FilterStatus, StatusFilterName } from '@/types/api';
import { WorkEventName, WorkPageAreaType } from '@/constant/gtm';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';

type StatusFilterPanelProps = {
  pageArea: WorkPageAreaType;
  title: string;
  filterName: StatusFilterName;
};

export const statusList: FilterStatus[] = [
  { name: '진행중', type: 0, value: 'P' },
  { name: '완료', type: 1, value: 'C' },
  { name: '반려', type: 2, value: 'R' },
  { name: '보류', type: 3, value: 'H' },
  { name: '취소', type: 4, value: 'X' },
];

/**
 * StatusFilter 의 편집 본문. 시안대로 체크박스 리스트(진행중/완료/반려/보류)로 표시.
 * 선택값은 전역 approvalFilter.statuses 를 직접 토글한다.
 */
const StatusFilterPanel = (props: StatusFilterPanelProps) => {
  const { pageArea, title, filterName } = props;
  const { t } = useTranslation();
  const workEventTracker = useWorkEventTracker(pageArea);
  const [filters, setFilters] = useAtom(approvalFilter);

  const toggle = (item: FilterStatus) => {
    const isChecked = filters[filterName].some(
      (res: FilterStatus) => res.type === item.type,
    );
    if (isChecked) {
      setFilters({
        ...filters,
        [filterName]: filters[filterName].filter(
          (res) => res.type !== item.type,
        ),
      });
    } else {
      setFilters({
        ...filters,
        [filterName]: [
          ...filters[filterName],
          { name: item.name, type: item.type, value: item.value },
        ],
      });
    }
    // GA Work - Approval 12번
    workEventTracker(WorkEventName.clickEvent, {
      area_name: title,
      contents: item.name,
      status: !isChecked,
      description: '품의 필터',
    });
  };

  return (
    <List>
      {statusList.map((item) => {
        const isChecked = filters[filterName].some(
          (res: FilterStatus) => res.type === item.type,
        );
        return (
          <Row
            key={`statusList-${item.type}`}
            className={'v2cb-hover-target'}
            onClick={() => toggle(item)}
          >
            <Checkbox checked={isChecked} sx={{ padding: 0 }} />
            <Label>{t(item.name, { context: 'status' })}</Label>
          </Row>
        );
      })}
    </List>
  );
};

export default StatusFilterPanel;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  * {
    cursor: pointer;
  }
`;

const Label = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: var(--dk-text, black);
`;
